/**
 * Signing in, in a browser, without a library.
 *
 * This is the OpenID Connect authorization-code flow with PKCE — the standard
 * one, the one every provider implements, and the one every guide reaches for a
 * package to do. There is no package here for a reason worth stating: the flow
 * is four steps, three of them are one line each, and the fourth is a `fetch`.
 * The libraries that wrap it are written for applications that need silent
 * renewal in hidden iframes, session monitoring and framework bindings, none of
 * which a game that opens a socket and holds it has any use for.
 *
 * What the flow actually is:
 *
 *  1. Make a random `verifier`, keep it, and send its SHA-256 to the provider
 *     along with the address to come back to. The provider never sees the
 *     verifier, only its hash.
 *  2. The player signs in on the provider's own page. The game never sees a
 *     password, which is the entire point of doing it this way — and is why
 *     there is no "create an account with a password" screen anywhere in this
 *     project, and should never be one.
 *  3. They come back with a one-time `code`. We exchange it for tokens,
 *     presenting the verifier: the hash proves the code came back to the same
 *     browser that asked for it, which is what stops a stolen code being worth
 *     anything.
 *  4. Keep the ID token. It is what the hearth's door checks and what the
 *     accounts module checks; both do it by signature, so neither of them has to
 *     trust this code at all.
 *
 * The client id is not a secret and is served to us by the game server. The
 * client *secret* is a different thing, belongs to a flow this game does not
 * use, and must never be in a browser bundle — see `server/hearth.ts`.
 */

const KEY_TOKENS = 'hearth.auth.tokens';
const KEY_FLIGHT = 'hearth.auth.inflight';
/** Renew this long before a token expires rather than after it has. */
const RENEW_MARGIN_MS = 60_000;

/** What the game server tells us about signing in. It knows; we ask. */
export interface AuthSetup {
  /** Whether this hearth asks who you are at all. */
  accounts: boolean;
  issuer?: string;
  clientId?: string;
  /**
   * The callback the provider will accept, when the operator has named one.
   *
   * Absent, a browser uses its own origin, which is right until the same game
   * answers on two of them — `hearthgame.co.uk` and `www.hearthgame.co.uk` are
   * one site to a player and two strings to an identity provider.
   */
  redirectUri?: string;
  /** Where the accounts module lives, for the account itself. */
  stdbUri?: string;
  stdbDb?: string;
}

/**
 * The provider said no, as against the provider not answering.
 *
 * A class rather than a flag on the message because the difference decides
 * whether a stored sign-in is thrown away, and a string comparison is a poor
 * thing to hang that on.
 */
class Refused extends Error {}

/** What came of trying to renew: kept, thrown away, or try again later. */
type Renewal = 'ok' | 'refused' | 'unreachable';

interface Tokens {
  id: string;
  refresh?: string;
  /** When the ID token stops being worth anything, in epoch milliseconds. */
  expires: number;
}

/** Who the provider says this is. Only ever read from a verified token's body. */
export interface Signed {
  subject: string;
  name: string;
  email: string;
}

/**
 * Move to the one address this hearth signs people in on, if this is not it.
 *
 * Returns true when it has started navigating, and the caller should stop: the
 * page is going away.
 *
 * The bug this exists for is invisible until it happens and obvious afterwards.
 * A sign-in is begun on whatever address the player typed — `www.example.com`,
 * say — and the verifier that proves the returning code belongs to this browser
 * is put in `sessionStorage`, which is *per origin*. The provider then sends
 * them back to the one callback it has registered, `example.com`, where that
 * verifier does not exist. The exchange fails, nobody is signed in, and the only
 * symptom is that you have to press the button a second time — on the second
 * attempt you are already on the canonical address, so it works, and the whole
 * thing looks like a flaky first click.
 *
 * So: one origin, decided by the operator, before anything is stored anywhere.
 * No loop is possible because the test is equality with that origin.
 */
export function goCanonical(setup: AuthSetup): boolean {
  if (!setup.accounts || !setup.redirectUri) return false;
  let target: URL;
  try {
    target = new URL(setup.redirectUri);
  } catch {
    return false;
  }
  if (target.origin === location.origin) return false;
  location.replace(`${target.origin}${location.pathname}${location.search}${location.hash}`);
  return true;
}

/** Ask the server what it wants. One fetch, at boot, before any room exists. */
export async function authSetup(): Promise<AuthSetup> {
  try {
    const res = await fetch('/auth-config', { cache: 'no-store' });
    if (!res.ok) throw new Error(String(res.status));
    return (await res.json()) as AuthSetup;
  } catch {
    // A hearth that cannot be asked is a hearth that is not asking: the socket
    // will say so itself the moment somebody tries to take a seat.
    return { accounts: false };
  }
}

export class SignIn {
  private tokens: Tokens | null = null;

  constructor(private readonly setup: AuthSetup) {
    this.tokens = read();
  }

  /**
   * Where the provider sends people back to.
   *
   * The server's answer if it has one, this page otherwise. It must be the same
   * string in the authorization request and in the token exchange — providers
   * check that the two agree, and rightly — which is why both go through here
   * rather than each working it out.
   */
  private get callback(): string {
    return this.setup.redirectUri || redirectUri();
  }

  get configured(): boolean {
    return !!(this.setup.accounts && this.setup.issuer && this.setup.clientId);
  }

  /**
   * Is somebody signed in here?
   *
   * **A live refresh token counts, and that is the whole of "remember me".**
   * This used to read `expires > Date.now()` against the *ID* token, which
   * providers issue with an hour on it. The refresh token beside it is good for
   * days or weeks. So a player who signed in last night came back to a Sign in
   * button while the browser was holding everything needed to keep them in —
   * and pressing it bounced them through the provider, who still knew them, and
   * returned them signed in without a word. The whole round trip was the client
   * failing to ask itself a question it could already answer.
   *
   * This getter is synchronous because the account bar paints from it. The
   * renewal it implies is done by `resume()` at boot and by `token()` at the
   * moment a token is actually needed; if the refresh token turns out to be
   * dead, those sign out and the bar repaints to the door.
   */
  get signedIn(): boolean {
    if (!this.tokens) return false;
    return this.tokens.expires > Date.now() || !!this.tokens.refresh;
  }

  /**
   * Come back to a tab that was signed in before, without asking anybody.
   *
   * Called once at boot, after `finish()` has had its chance at a code in the
   * address bar. Returns whether somebody is signed in when it is done.
   *
   * The distinction it rests on is between a provider that *refused* and a
   * provider that could not be *reached*. A refusal means the refresh token is
   * spent or revoked and the honest thing is to forget it. Being unreachable
   * means the aeroplane wifi is down, and throwing away a perfectly good
   * sign-in over that would be the same bug in a new coat.
   */
  async resume(): Promise<boolean> {
    if (!this.tokens) return false;
    if (this.tokens.expires - RENEW_MARGIN_MS > Date.now()) return true;
    if (!this.tokens.refresh) {
      this.signOut();
      return false;
    }
    const how = await this.renew();
    if (how === 'refused') this.signOut();
    return how === 'ok';
  }

  /** Who they are, as the provider described them. Null when nobody is. */
  get who(): Signed | null {
    if (!this.tokens) return null;
    const body = claims(this.tokens.id);
    if (!body) return null;
    return {
      subject: String(body.sub ?? ''),
      name: typeof body.name === 'string' ? body.name : '',
      email: typeof body.email === 'string' ? body.email : '',
    };
  }

  /**
   * The token to hand over, renewed first if it is about to run out.
   *
   * Callers ask for this at the moment they need it — opening a socket, calling
   * a reducer — rather than holding on to one, because "the token I fetched
   * when the menu opened" is exactly the token that expires while somebody
   * decides which kingdom to play.
   */
  async token(): Promise<string | null> {
    if (!this.tokens) return null;
    if (this.tokens.expires - RENEW_MARGIN_MS > Date.now()) return this.tokens.id;
    if (this.tokens.refresh) {
      const how = await this.renew();
      if (how === 'ok') return this.tokens.id;
      // Unreachable is not refused. Say "no token just now" and leave the
      // sign-in where it is, so the next attempt — when the network is back —
      // finds it. Signing out here would make a dropped connection cost a
      // player their evening's login.
      if (how === 'unreachable') return null;
    }
    // Refused, or expired with nothing to renew from: they are signed out, and
    // saying so now is kinder than a socket refusing them in a moment.
    this.signOut();
    return null;
  }

  /**
   * Leave for the provider's sign-in page. This call does not return: the tab
   * goes elsewhere and comes back through `finishSignIn`.
   */
  async begin(): Promise<void> {
    if (!this.configured) throw new Error('this hearth is not asking for accounts');
    const verifier = random(64);
    const state = random(16);
    sessionStorage.setItem(KEY_FLIGHT, JSON.stringify({ verifier, state, at: Date.now() }));
    const url = new URL(`${this.setup.issuer}/auth`);
    url.searchParams.set('client_id', this.setup.clientId as string);
    url.searchParams.set('redirect_uri', this.callback);
    url.searchParams.set('response_type', 'code');
    // `offline_access` is what earns a refresh token, which is what keeps a
    // player signed in across an evening rather than an hour.
    url.searchParams.set('scope', 'openid profile email offline_access');
    url.searchParams.set('state', state);
    url.searchParams.set('code_challenge', await sha256(verifier));
    url.searchParams.set('code_challenge_method', 'S256');
    location.assign(url.toString());
  }

  /**
   * Finish a sign-in if this page load is the way back from one.
   *
   * Returns true when it took a code off the URL and turned it into tokens, so
   * the caller knows this load was a return rather than an arrival. The query
   * string is cleared either way: a spent authorization code left in the address
   * bar is a thing that gets pasted into chats.
   */
  async finish(): Promise<boolean> {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const failed = params.get('error');
    if (!code && !failed) return false;
    const flight = JSON.parse(sessionStorage.getItem(KEY_FLIGHT) ?? 'null') as {
      verifier: string;
      state: string;
    } | null;
    sessionStorage.removeItem(KEY_FLIGHT);
    scrub();
    if (failed) throw new Error(describe(failed, params.get('error_description')));
    if (!flight) throw new Error('that sign-in did not start here');
    // The state is the guard against somebody else's authorization response
    // being fed to this tab. Mismatched, it is not ours, and it is not used.
    if (state !== flight.state) throw new Error('that sign-in came back wrong');
    await this.exchange({
      grant_type: 'authorization_code',
      code: code as string,
      redirect_uri: this.callback,
      code_verifier: flight.verifier,
    });
    return true;
  }

  /** Forget the tokens. The provider's own session is its business. */
  signOut(): void {
    this.tokens = null;
    try {
      localStorage.removeItem(KEY_TOKENS);
    } catch {
      /* private browsing */
    }
  }

  private async renew(): Promise<Renewal> {
    try {
      await this.exchange({
        grant_type: 'refresh_token',
        refresh_token: this.tokens?.refresh as string,
      });
      return 'ok';
    } catch (e) {
      return e instanceof Refused ? 'refused' : 'unreachable';
    }
  }

  /** The one call that actually gets tokens, for both ways of getting them. */
  private async exchange(body: Record<string, string>): Promise<void> {
    let res: Response;
    try {
      res = await fetch(`${this.setup.issuer}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ ...body, client_id: this.setup.clientId as string }),
      });
    } catch {
      throw new Error('could not reach the identity provider');
    }
    /**
     * 4xx is the provider saying no to *this credential*; 5xx is the provider
     * having a bad day. Only the first is a reason to forget a sign-in, and
     * collapsing the two is how a five-minute outage logs everybody out.
     */
    if (res.status >= 400 && res.status < 500) {
      throw new Refused('the identity provider refused that sign-in');
    }
    if (!res.ok) throw new Error('the identity provider could not answer just now');
    const got = (await res.json()) as {
      id_token?: string;
      refresh_token?: string;
      expires_in?: number;
    };
    if (!got.id_token) throw new Error('that sign-in carried no identity');
    const body2 = claims(got.id_token);
    const exp = typeof body2?.exp === 'number' ? body2.exp * 1000 : 0;
    this.tokens = {
      id: got.id_token,
      refresh: got.refresh_token ?? this.tokens?.refresh,
      // The token's own expiry wins over the response's, because it is the one
      // every verifier will actually read.
      expires: exp || Date.now() + (got.expires_in ?? 3600) * 1000,
    };
    write(this.tokens);
  }
}

/**
 * The address the provider sends people back to.
 *
 * The page itself, with nothing on it. It has to match one of the redirect URIs
 * registered for the client exactly — including the trailing slash and the port
 * — which is the single most common reason a first sign-in fails, and worth
 * knowing before spending an afternoon on it.
 */
export function redirectUri(): string {
  return `${location.origin}${location.pathname}`;
}

/** Take the sign-in's leavings out of the address bar without reloading. */
function scrub(): void {
  const url = new URL(location.href);
  for (const k of ['code', 'state', 'error', 'error_description', 'iss', 'session_state']) {
    url.searchParams.delete(k);
  }
  history.replaceState(null, '', url.toString());
}

/** A provider's error codes are for logs; this is for a person. */
function describe(code: string, detail: string | null): string {
  if (code === 'access_denied') return 'that sign-in was cancelled';
  return detail ? `${code}: ${detail}` : code;
}

/**
 * The body of a token, read without checking it.
 *
 * Safe *because* nothing here decides anything: the hearth and the accounts
 * module both verify the signature themselves, and this is only ever used to
 * know when to renew and what to put on screen. A client that lied to itself
 * about its own token would get a room's door shut in its face.
 */
function claims(jwt: string): Record<string, unknown> | null {
  try {
    const part = jwt.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function read(): Tokens | null {
  try {
    const held = localStorage.getItem(KEY_TOKENS);
    return held ? (JSON.parse(held) as Tokens) : null;
  } catch {
    return null;
  }
}

function write(t: Tokens): void {
  try {
    localStorage.setItem(KEY_TOKENS, JSON.stringify(t));
  } catch {
    /* private browsing: this session only, which still works */
  }
}

/** A URL-safe random string, from the browser's own randomness and no other. */
function random(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64url(buf);
}

async function sha256(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return base64url(new Uint8Array(digest));
}

function base64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
